import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Avatar,
  Chip,
  IconButton,
  Switch,
  InputAdornment,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Tooltip,
  Autocomplete,
  Divider,
} from '@mui/material';
import { FaUsers } from 'react-icons/fa';
import { IoSearch } from 'react-icons/io5';
import { MdAdd, MdEdit } from 'react-icons/md';
import { useSnackbar } from 'notistack';
import {
  getManagementUserDetails,
  updateUserStatus,
  createUser,
  getGroups,
  updateUserGroups,
} from "../api's/ManagementApi";

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
  const colors = [
    '#2196F3', // blue
    '#4CAF50', // green
    '#FF9800', // orange
    '#9C27B0', // purple
    '#F44336', // red
    '#00BCD4', // cyan
    '#FFEB3B', // yellow
  ];
  if (!name) return colors[0];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const getGroupColor = (groupName) => {
  // Array of color schemes for groups
  const colorSchemes = [
    { bg: '#E3F2FD', color: '#1976D2' }, // Blue
    { bg: '#F3E5F5', color: '#7B1FA2' }, // Purple
    { bg: '#E8F5E9', color: '#388E3C' }, // Green
    { bg: '#FFF3E0', color: '#F57C00' }, // Orange
    { bg: '#FCE4EC', color: '#C2185B' }, // Pink
    { bg: '#EDE7F6', color: '#5E35B1' }, // Deep Purple
    { bg: '#E0F2F1', color: '#00897B' }, // Teal
    { bg: '#FFF9C4', color: '#F9A825' }, // Yellow
    { bg: '#FFEBEE', color: '#D32F2F' }, // Red
    { bg: '#E8EAF6', color: '#3F51B5' }, // Indigo
  ];

  // If no name provided, return default gray
  if (!groupName) {
    return { bg: '#F5F5F5', color: '#616161' };
  }

  // Generate a consistent index based on the group name
  let hash = 0;
  for (let i = 0; i < groupName.length; i++) {
    hash = hash * 31 + groupName.charCodeAt(i);
  }

  // Get color based on hash
  const index = Math.abs(hash) % colorSchemes.length;
  return colorSchemes[index];
};

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [groupFilter, setGroupFilter] = useState('All');
  // const [roleFilter, setRoleFilter] = useState('Any');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    fullName: '',
    email: '',
    username: '',
    // password: '',
    isActive: true,
    groups: [],
  });
  const [savingUser, setSavingUser] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const userPermissions = JSON.parse(sessionStorage.getItem('userPermissions') || '[]');
  const hasUpdateStatusPermission = userPermissions.includes('UPDATE_USER_STATUS');

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, []);

  useEffect(() => {
    filterUsers();
    // }, [users, searchTerm, statusFilter, roleFilter]);
  }, [users, searchTerm, statusFilter, groupFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getManagementUserDetails();
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      enqueueSnackbar('Failed to fetch users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await getGroups();
      setGroups(response.result || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch groups', { variant: 'error' });
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter((user) => user.isActive === (statusFilter === 'true'));
    }

    // Group filter
    if (groupFilter !== 'All') {
      const groupIdToMatch = groupFilter;
      filtered = filtered.filter((user) =>
        user.groups?.some(
          (g) => String(g.groupId) === String(groupIdToMatch) || String(g.group_id) === String(groupIdToMatch)
        )
      );
    }

    // // Role filter
    // if (roleFilter !== 'Any') {
    //   filtered = filtered.filter((user) => user.roles?.some((role) => role.roleName === roleFilter));
    // }

    setFilteredUsers(filtered);
    setPage(0);
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      setUpdatingUserId(userId);
      const newStatus = !currentStatus;
      await updateUserStatus(userId, newStatus);
      setUsers((prevUsers) =>
        prevUsers.map((user) => (user.user_id === userId ? { ...user, isActive: newStatus } : user))
      );

      enqueueSnackbar(`User status updated to ${newStatus ? 'Active' : 'Inactive'}`, {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      enqueueSnackbar('Failed to update user status', { variant: 'error' });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleAddUser = () => {
    setAddUserDialogOpen(true);
  };

  const handleCloseAddUserDialog = () => {
    setAddUserDialogOpen(false);
    setNewUserData({
      fullName: '',
      email: '',
      username: '',
      // password: '',
      isActive: true,
      groups: [],
    });
  };

  const handleNewUserInputChange = (field, value) => {
    setNewUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRemoveNewUserGroup = (groupId) => {
    setNewUserData((prev) => ({
      ...prev,
      groups: prev.groups.filter((r) => r.groupId !== groupId),
    }));
  };

  const handleSaveNewUser = async () => {
    // Validate required fields
    if (!newUserData.fullName.trim()) {
      enqueueSnackbar('Full Name is required', { variant: 'error' });
      return;
    }
    if (!newUserData.email.trim()) {
      enqueueSnackbar('Email is required', { variant: 'error' });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserData.email.trim())) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'error' });
      return;
    }
    // if (!newUserData.username.trim()) {
    //   enqueueSnackbar('Username is required', { variant: 'error' });
    //   return;
    // }
    // if (!newUserData.password.trim()) {
    //   enqueueSnackbar('Password is required', { variant: 'error' });
    //   return;
    // }
    // if (newUserData.group.length === 0) {
    //   enqueueSnackbar('Please select at least one group', { variant: 'error' });
    //   return;
    // }

    setSavingUser(true);
    try {
      const payload = {
        username: newUserData.email,
        email: newUserData.email,
        // password: newUserData.password,
        user_group: 'Group1',
        full_name: newUserData.fullName,
        avatar_id: 3,
        isActive: newUserData.isActive,
        groups: newUserData.groups,
      };

      await createUser(payload);

      // Refresh the user list
      await fetchUsers();

      enqueueSnackbar('User added successfully', { variant: 'success' });
      handleCloseAddUserDialog();
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.response?.status === 409) {
        enqueueSnackbar(error.response?.data?.message || 'A user with the same email already exists', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(error.response?.data?.message || 'Failed to create user', { variant: 'error' });
      }
    } finally {
      setSavingUser(false);
    }
  };

  const handleEditGroup = (user) => {
    setEditingUser(user);
    setSelectedUserGroups(user.groups || []);
    setGroupDialogOpen(true);
  };

  const handleGroupToggle = (group) => {
    setSelectedUserGroups((prevGroups) => {
      const groupExists = prevGroups.some((g) => g.groupId === group.groupId);
      if (groupExists) {
        return prevGroups.filter((g) => g.groupId !== group.groupId);
      }
      return [...prevGroups, group];
    });
  };

  const handleSaveGroups = async () => {
    setSavingGroups(true);
    try {
      const groupIds = selectedUserGroups.map((group) => group.groupId);

      const response = await updateUserGroups(editingUser.user_id, groupIds);

      // Only update state if API returns 200 status
      if (response.status === 200) {
        // Update user groups in local state
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.user_id === editingUser.user_id ? { ...user, groups: selectedUserGroups } : user
          )
        );

        enqueueSnackbar(`User group${(groupIds.length > 1 && 's') || ''} updated successfully`, {
          variant: 'success',
        });
        setGroupDialogOpen(false);
        setEditingUser(null);
        setSelectedUserGroups([]);
      }
    } catch (error) {
      console.error('Error updating user groups:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to update user groups', { variant: 'error' });
    } finally {
      setSavingGroups(false);
    }
  };

  const handleCloseGroupDialog = () => {
    setGroupDialogOpen(false);
    setEditingUser(null);
    setSelectedUserGroups([]);
    setGroupSearchTerm('');
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setGroupFilter('All');
    // setRoleFilter('Any');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const filteredGroupsForDialog = groupSearchTerm.trim()
    ? groups.filter((group) => {
        const name = (group.groupName || group.name || '').toLowerCase();
        return name.includes(groupSearchTerm.trim().toLowerCase());
      })
    : groups;

  return (
    <Box sx={{ padding: '0px 16px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage access to data pipelines, governance tools and admin features.
          </Typography>
        </Box>
        <Button
          type="submit"
          variant="contained"
          sx={{ backgroundColor: '#00043C', '&:hover': { backgroundColor: '#00043C' } }}
          onClick={handleAddUser}
        >
          <MdAdd />
          Add New User
        </Button>
      </Box>

      <Paper sx={{ mb: 2, p: 1.5, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoSearch />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Groups</InputLabel>
            <Select value={groupFilter} label="Groups" onChange={(e) => setGroupFilter(e.target.value)}>
              <MenuItem value="All">All</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.groupId} value={group.groupId}>
                  {group.groupName || group.name || 'Unnamed Group'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
          {/* <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)}>
              <MenuItem value="Any">Any</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="READER">Reader</MenuItem>
              <MenuItem value="EXECUTOR">Executor</MenuItem>
            </Select>
          </FormControl> */}
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            size="small"
            sx={{
              textTransform: 'none',
              borderColor: '#43425D',
              color: '#43425D',
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#43425D',
                backgroundColor: 'rgba(67, 66, 93, 0.04)',
              },
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e8e8e8',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress size={40} thickness={3.5} sx={{ color: '#00043C' }} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  USER
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  EMAIL
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  GROUPS
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  STATUS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow
                  key={user.user_id}
                  hover
                  sx={{
                    '& td': { py: 1.5 },
                    '&:hover': {
                      backgroundColor: '#f0f2ff',
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(user.full_name),
                          width: 36,
                          height: 36,
                          fontSize: '13px',
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(user.full_name)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {user.full_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {user.groups && user.groups.length > 0 ? (
                        <>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {user.groups.map((group, index) => {
                              const groupName = group.groupName || group.name || 'Unnamed Group';
                              const groupStyle = getGroupColor(groupName);
                              return (
                                <Chip
                                  key={index}
                                  label={groupName}
                                  size="small"
                                  sx={{
                                    backgroundColor: groupStyle.bg,
                                    color: groupStyle.color,
                                    fontWeight: 500,
                                    fontSize: '11px',
                                    height: '22px',
                                  }}
                                />
                              );
                            })}
                          </Box>
                          <Tooltip title="Edit groups" arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleEditGroup(user)}
                              sx={{
                                width: '28px',
                                height: '28px',
                                color: '#00043C',
                                backgroundColor: '#f0f2ff',
                                border: '1px solid #e8eaff',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#e8eaff',
                                  borderColor: '#00043C',
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 2px 8px rgba(0, 4, 60, 0.25)',
                                },
                              }}
                            >
                              <MdEdit size={14} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Assign this user to a group" arrow placement="top">
                          <Chip
                            icon={<MdAdd size={14} />}
                            label="Add to group"
                            onClick={() => handleEditGroup(user)}
                            size="small"
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              fontWeight: 500,
                              fontSize: '12px',
                              height: '28px',
                              borderColor: '#c4c4c4',
                              color: '#555',
                              backgroundColor: '#fafafa',
                              '&:hover': {
                                borderColor: '#00043C',
                                color: '#00043C',
                                backgroundColor: '#f0f2ff',
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {updatingUserId === user.user_id ? (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      ) : (
                        <Switch
                          checked={user.isActive}
                          onChange={() => handleStatusToggle(user.user_id, user.isActive)}
                          size="small"
                          disabled={updatingUserId !== null || !hasUpdateStatusPermission}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4CAF50',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4CAF50',
                            },
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: user.isActive ? '#4CAF50' : '#9E9E9E',
                          fontWeight: 500,
                          fontSize: '13px',
                        }}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && filteredUsers.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      <Dialog
        open={groupDialogOpen}
        onClose={handleCloseGroupDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: '#00043C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(0, 4, 60, 0.25)',
              }}
            >
              <FaUsers style={{ color: 'white', fontSize: '20px' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#1a1a1a',
                  mb: 0.5,
                }}
              >
                Manage Groups
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontSize: '14px',
                }}
              >
                {editingUser?.full_name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2 }}>
          <Box
            sx={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              p: 2,
              mb: 3,
              border: '1px solid #e8e8e8',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: '13px',
                lineHeight: 1.6,
              }}
            >
              Select or deselect groups to control this user's access permissions and data visibility.
            </Typography>
          </Box>

          <TextField
            placeholder="Search groups..."
            value={groupSearchTerm}
            onChange={(e) => setGroupSearchTerm(e.target.value)}
            size="small"
            fullWidth
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'white',
                '&:hover fieldset': { borderColor: '#00043C' },
                '&.Mui-focused fieldset': { borderColor: '#00043C', borderWidth: '2px' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoSearch />
                </InputAdornment>
              ),
            }}
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1.5,
              color: '#999',
              textTransform: 'uppercase',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.8px',
            }}
          >
            Available Groups ({filteredGroupsForDialog.length}
            {groupSearchTerm ? ` of ${groups.length}` : ''})
          </Typography>

          <Box
            sx={{
              maxHeight: '320px',
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#d0d0d0',
                borderRadius: '4px',
                '&:hover': {
                  background: '#b0b0b0',
                },
              },
            }}
          >
            <FormGroup>
              {filteredGroupsForDialog.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#666', py: 2, textAlign: 'center' }}>
                  {groupSearchTerm.trim() ? 'No groups match your search.' : 'No groups available.'}
                </Typography>
              ) : (
                filteredGroupsForDialog.map((group) => {
                  const isChecked = selectedUserGroups.some((g) => g.groupId === group.groupId);
                  const groupName = group.groupName || group.name || 'Unnamed Group';
                  const groupColors = getGroupColor(groupName);

                  return (
                    <Box
                      key={group.groupId}
                      sx={{
                        mb: 1,
                        borderRadius: '10px',
                        border: isChecked ? '2px solid #00043C' : '2px solid #e8e8e8',
                        backgroundColor: isChecked ? '#f0f2ff' : 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: isChecked ? '#00043C' : '#d0d0d0',
                          backgroundColor: isChecked ? '#f0f2ff' : '#fafafa',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => handleGroupToggle(group)}
                            sx={{
                              '&.Mui-checked': {
                                color: '#00043C',
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              py: 0.5,
                              flex: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: groupColors.color,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: isChecked ? 600 : 500,
                                fontSize: '14px',
                                color: '#1a1a1a',
                                flex: 1,
                              }}
                            >
                              {groupName}
                            </Typography>
                            {isChecked && (
                              <Chip
                                label="Assigned"
                                size="small"
                                sx={{
                                  backgroundColor: '#00043C',
                                  color: 'white',
                                  height: '22px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  letterSpacing: '0.3px',
                                  boxShadow: '0 2px 6px rgba(0, 4, 60, 0.25)',
                                }}
                              />
                            )}
                          </Box>
                        }
                        sx={{
                          m: 0,
                          width: '100%',
                          px: 1.5,
                          py: 0.5,
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </FormGroup>
          </Box>

          {selectedUserGroups.length > 0 && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: '#f0f2ff',
                borderRadius: '10px',
                border: '1px solid #e0e6ff',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#00043C',
                  fontSize: '12px',
                  fontWeight: 600,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {selectedUserGroups.length} {selectedUserGroups.length === 1 ? 'group' : 'groups'} selected
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#666',
                  fontSize: '11px',
                }}
              >
                User will have access to all resources in the selected groups
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 1.5,
            borderTop: '1px solid #e8e8e8',
            backgroundColor: '#fafafa',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCloseGroupDialog}
            disabled={savingGroups}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              color: '#43425D',
              border: '1px solid #43425D',
              '&:hover': {
                borderColor: '#43425D',
                backgroundColor: 'rgba(67, 66, 93, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveGroups}
            variant="contained"
            sx={{ backgroundColor: '#00043C', '&:hover': { backgroundColor: '#00043C' } }}
            disabled={savingGroups}
          >
            {savingGroups ? (
              <>
                <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addUserDialogOpen}
        onClose={handleCloseAddUserDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '24px',
              color: '#1a1a1a',
              mb: 0.5,
            }}
          >
            Add New User
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: '14px',
              lineHeight: 1.6,
            }}
          >
            Create a new user account and assign access permissions
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ px: 3, pt: 3 }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Left Column - User Information */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    fontSize: '13px',
                    color: '#1a1a1a',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  User Information
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '13px',
                    }}
                  >
                    Full Name *
                  </Typography>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="e.g. Alice Johnson"
                    value={newUserData.fullName}
                    onChange={(e) => handleNewUserInputChange('fullName', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': {
                          borderColor: '#00043C',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#00043C',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '13px',
                    }}
                  >
                    Email Address *
                  </Typography>
                  <TextField
                    fullWidth
                    size="medium"
                    type="email"
                    placeholder="alice@example.com"
                    value={newUserData.email}
                    onChange={(e) => handleNewUserInputChange('email', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': {
                          borderColor: '#00043C',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#00043C',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Right Column - Account & Access */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    fontSize: '13px',
                    color: '#1a1a1a',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Account & Access
                </Typography>

                {/* Account Status Toggle */}
                <Box
                  sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: '10px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e8e8e8',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                          fontSize: '13px',
                          mb: 0.5,
                        }}
                      >
                        Account Status
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '12px',
                        }}
                      >
                        {newUserData.isActive ? 'User can sign in' : 'Access disabled'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: newUserData.isActive ? '#4CAF50' : '#9E9E9E',
                          fontWeight: 600,
                          fontSize: '12px',
                        }}
                      >
                        {newUserData.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                      <Switch
                        checked={newUserData.isActive}
                        onChange={(e) => handleNewUserInputChange('isActive', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#4CAF50',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#4CAF50',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Groups Autocomplete */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '13px',
                    }}
                  >
                    Groups
                  </Typography>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={groups}
                    value={newUserData.groups}
                    onChange={(event, newValue) => {
                      handleNewUserInputChange('groups', newValue);
                    }}
                    getOptionLabel={(option) => option.groupName || option.name || 'Unnamed Group'}
                    isOptionEqualToValue={(option, value) => option.groupId === value.groupId}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={newUserData.groups.length === 0 ? 'Select groups...' : ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '&:hover fieldset': {
                              borderColor: '#00043C',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#00043C',
                              borderWidth: '2px',
                            },
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const groupName = option.groupName || option.name || 'Unnamed Group';
                        const groupStyle = getGroupColor(groupName);
                        return (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.groupId}
                            label={groupName}
                            size="small"
                            sx={{
                              backgroundColor: groupStyle.bg,
                              color: groupStyle.color,
                              fontWeight: 600,
                              fontSize: '12px',
                              height: '26px',
                              '& .MuiChip-deleteIcon': {
                                color: groupStyle.color,
                                fontSize: '16px',
                                '&:hover': {
                                  color: groupStyle.color,
                                  opacity: 0.7,
                                },
                              },
                            }}
                          />
                        );
                      })
                    }
                    renderOption={(props, option, { selected }) => {
                      const groupName = option.groupName || option.name || 'Unnamed Group';
                      const groupColors = getGroupColor(groupName);
                      return (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1,
                            '&:hover': {
                              backgroundColor: '#f0f2ff',
                            },
                          }}
                        >
                          <Checkbox
                            checked={selected}
                            sx={{
                              padding: 0,
                              '&.Mui-checked': {
                                color: '#00043C',
                              },
                            }}
                          />
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: groupColors.color,
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontSize: '14px', flex: 1 }}>{groupName}</Typography>
                        </Box>
                      );
                    }}
                    sx={{
                      '& .MuiAutocomplete-tag': {
                        margin: '2px',
                      },
                    }}
                  />
                  {newUserData.groups.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#999',
                        fontSize: '11px',
                        mt: 0.5,
                        display: 'block',
                      }}
                    >
                      User will have access to resources in selected groups
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#00043C',
                        fontSize: '11px',
                        mt: 0.5,
                        display: 'block',
                        fontWeight: 600,
                      }}
                    >
                      {newUserData.groups.length} {newUserData.groups.length === 1 ? 'group' : 'groups'} selected
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 1.5,
            backgroundColor: '#fafafa',
          }}
        >
          <Button
            onClick={handleCloseAddUserDialog}
            variant="outlined"
            disabled={savingUser}
            sx={{ textTransform: 'capitalize', color: '#43425D', border: '1px solid #43425D' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              px: 4,
              py: 1,
              borderRadius: '8px',
              backgroundColor: '#00043C',
              boxShadow: '0 2px 8px rgba(0, 4, 60, 0.25)',
              '&:hover': {
                backgroundColor: '#000652',
                boxShadow: '0 4px 12px rgba(0, 4, 60, 0.35)',
              },
            }}
            disabled={savingUser}
            onClick={handleSaveNewUser}
          >
            {savingUser ? (
              <>
                <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create User'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {!loading && filteredUsers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <FaUsers style={{ fontSize: 48, color: '#00043C', opacity: 0.5, marginBottom: 12 }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontSize: '16px' }}>
            No users found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
            Try adjusting your search or filters
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default UsersPage;
